import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Gamepad2, Trophy } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { useBreadcrumb } from '../contexts/BreadcrumbContext';
import { eventsAPI } from '../services/api';
import { tournamentAPI } from '../tournaments/services/tournamentApi';
import { transformTournamentData } from '../tournaments/utils/tournamentHelpers';
import GamesList from '../components/common/GamesList';
import TournamentCard from '../tournaments/components/TournamentCard';

const MyGamesPage = () => {
  const { theme } = useTheme();
  const navigate = useNavigate();
  const { user, isAuthenticated, authChecked } = useAuth();
  const { setBreadcrumbItems } = useBreadcrumb();

  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    total: 0,
    limit: 20,
    offset: 0,
    hasMore: false
  });

  // Tournaments the current user has played in (organizer, direct player, or
  // via a team roster) — shown as a row of cards above the games list.
  const [pastTournaments, setPastTournaments] = useState([]);
  const [tournamentsLoading, setTournamentsLoading] = useState(true);

  // Independent show/hide toggles for each section — both on by default.
  const [showTournaments, setShowTournaments] = useState(true);
  const [showGames, setShowGames] = useState(true);

  // Redirect to signin if not authenticated
  useEffect(() => {
    if (authChecked && !isAuthenticated) {
      navigate('/signin', { replace: true });
    }
  }, [authChecked, isAuthenticated, navigate]);

  // Set breadcrumb
  useEffect(() => {
    setBreadcrumbItems([
      { label: 'Home', path: '/' },
      { label: 'My Games' }
    ]);
    return () => {
      setBreadcrumbItems([]);
    };
  }, [setBreadcrumbItems]);

  // Fetch games
  useEffect(() => {
    const fetchGames = async () => {
      if (!authChecked || !isAuthenticated) return;

      setLoading(true);
      setError(null);

      try {
        const result = await eventsAPI.getMyGames({
          limit: pagination.limit,
          offset: pagination.offset
        });
        setGames(result?.games || []);
        setPagination(prev => ({
          ...prev,
          total: result?.pagination?.total || 0,
          hasMore: result?.pagination?.hasMore || false
        }));
      } catch (err) {
        console.error('Failed to fetch games:', err);
        setError('Failed to load games. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchGames();
  }, [authChecked, isAuthenticated, pagination.limit, pagination.offset]);

  // Fetch tournaments the user has played in (completed only)
  useEffect(() => {
    const fetchPastTournaments = async () => {
      if (!authChecked || !isAuthenticated || !user?.id) return;

      setTournamentsLoading(true);
      try {
        const result = await tournamentAPI.getAll({
          involvedUserId: user.id,
          status: 'completed',
          limit: 50
        });
        setPastTournaments((result?.data || []).map(transformTournamentData));
      } catch (err) {
        console.error('Failed to fetch past tournaments:', err);
      } finally {
        setTournamentsLoading(false);
      }
    };

    fetchPastTournaments();
  }, [authChecked, isAuthenticated, user?.id]);

  const handleTournamentClick = (tournament) => {
    navigate(`/tournament/${tournament.id}?tab=standings`);
  };

  // Load more games
  const loadMore = () => {
    setPagination(prev => ({
      ...prev,
      offset: prev.offset + prev.limit
    }));
  };

  // Retry function
  const handleRetry = () => {
    window.location.reload();
  };

  // Don't render if auth not checked or not authenticated
  if (!authChecked || !isAuthenticated) {
    return null;
  }

  return (
    <div className={`${theme.background} min-h-screen`}>
      <div className="max-w-7xl mx-auto px-4 lg:px-0 py-6">
        {/* Page Title */}
        <div className="flex items-center gap-x-3 mb-6">
          <div className="p-2 bg-blue-100 rounded-lg">
            <Gamepad2 className="w-6 h-6 text-blue-600" />
          </div>
          <div>
            <h1 className={`text-2xl font-bold ${theme.text}`}>My Games</h1>
            <p className={`text-sm ${theme.textMuted}`}>
              {pagination.total > 0
                ? `${pagination.total} game${pagination.total !== 1 ? 's' : ''} total`
                : 'View all your game history'}
            </p>
          </div>
        </div>

        {/* Section filters — both on by default */}
        {(!tournamentsLoading && pastTournaments.length > 0) && (
          <div className="flex items-center gap-6 mb-6">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={showTournaments}
                onChange={(e) => setShowTournaments(e.target.checked)}
                className="w-4 h-4 rounded"
              />
              <span className={`text-sm ${theme.textSecondary}`}>Show Tournaments</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={showGames}
                onChange={(e) => setShowGames(e.target.checked)}
                className="w-4 h-4 rounded"
              />
              <span className={`text-sm ${theme.textSecondary}`}>Show Games</span>
            </label>
          </div>
        )}

        {/* My Tournaments — shown above the games list when present */}
        {showTournaments && !tournamentsLoading && pastTournaments.length > 0 && (
          <div className="mb-8">
            <div className="flex items-center gap-x-2 mb-4">
              <Trophy className="w-5 h-5 text-blue-600" />
              <h2 className={`text-lg font-semibold ${theme.text}`}>My Tournaments</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {pastTournaments.map(tournament => (
                <TournamentCard
                  key={tournament.id}
                  tournament={tournament}
                  onView={handleTournamentClick}
                />
              ))}
            </div>
          </div>
        )}

        {/* Games List */}
        {showGames && (
          <GamesList
            games={games}
            loading={loading}
            error={error}
            onRetry={handleRetry}
            emptyTitle="No games yet"
            emptyMessage="Join events and participate in games to see your game history here. Your completed and ongoing games will appear on this page."
            pagination={pagination}
            onLoadMore={loadMore}
          />
        )}
      </div>
    </div>
  );
};

export default MyGamesPage;
